import { gql } from "@apollo/client";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { isArray } from "lodash";
import { useState } from "react";
import {
  useCreateProgramEligibilityQuestionMutation,
  CreateProgramEligibilityQuestionMutationVariables as Question,
  EligibilityQuestionAnswerFormatTypeEnum as AnswerFormatType,
} from "~/graphql/__generated__/graphql";

import { ANSWER_MODULE_REGISTRY } from "@caredna-marlin/utils/answerModules/registry"
import QuestionForm, { predicateValid } from "./QuestionForm";

type CreateProgramDialogProps = {
  programId: number;
  answerFormatType: AnswerFormatType
  onClose: () => void;
};

const CreateQuestionDialog = ({
  onClose,
  programId,
  answerFormatType,
}: CreateProgramDialogProps) => {
  const [local, setLocal] = useState<Question>(() => ({
    programEligibilityQuestions: [{
      programId,
      disqualifyingAnswers: [],
    }],
    answerFormatType,
    answerModule: null,
    answerModulePredicate: null,
    question: "",
    answerOptions: [],
    useForPreScoreCheck: false,
  }));

  const [createItem, { loading }] = useCreateProgramEligibilityQuestionMutation()

  const usesPredicate = !!local.answerModule && !!ANSWER_MODULE_REGISTRY[local.answerModule].examplePredicate
  const usesAnswerOptionList = local.answerFormatType === "selected_option" || local.answerFormatType === "multi_select_options";

  const handleSubmit = async () => {
    await createItem({
      variables: {
        ...local,
        // If the answer module is null, or it doesn't use a predicate, clear it out.
        answerModulePredicate: (local.answerModule && usesPredicate) ? local.answerModulePredicate : null,
        answerOptions: (usesAnswerOptionList && isArray(local.answerOptions)) ? local.answerOptions.filter(a => !!a) : null,
      },
      awaitRefetchQueries: true,
      refetchQueries: ["OrgConfig"],
    });
    onClose();
  };

  return (
    <Dialog open={true} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Eligibility Question</DialogTitle>
      <DialogContent>
        <QuestionForm local={local} setLocal={setLocal} loading={loading} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} loading={loading} disabled={!local.question || !predicateValid(local)}>
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateQuestionDialog;

gql(`
  mutation CreateProgramEligibilityQuestion(
    $programEligibilityQuestions: [ProgramEligibilityQuestionProgramInput!]!
    $question: String!
    $answerFormatType: EligibilityQuestionAnswerFormatTypeEnum!
    $answerModule: EligibilityQuestionAnswerModuleEnum
    $answerModulePredicate: String
    $answerOptions: [CreateAnswerOption!]
    $useForPreScoreCheck: Boolean!
  )
  @permission(requires: manageOrgConfig)
  {
    createProgramEligibilityQuestion(
        programEligibilityQuestions: $programEligibilityQuestions,
        question: $question,
        answerFormatType: $answerFormatType,
        answerModule: $answerModule,
        answerModulePredicate: $answerModulePredicate,
        answerOptions: $answerOptions
        useForPreScoreCheck: $useForPreScoreCheck,
    ) {
      id
    }
  }
`);
